import { Fragment } from 'react';
import { Link } from 'react-router-dom';

const TICKET_REFERENCE = /\bT-(\d+)\b/gi;

export const ticketKey = (ticketNumber: number): string => `T-${ticketNumber}`;

export const ticketUrl = (projectId: number, ticketNumber: number): string =>
  `/projects/${projectId}?tab=tickety&ticket=${ticketNumber}`;

// Every ticket picker in the app lists tickets by their key (T-12), so they are
// ordered by that number too — the board's priority/due-date ordering makes a
// dropdown look shuffled when you are looking for one number in it. Tickets
// without a number (personal tasks) sink to the bottom.
export const byTicketNumber = <T extends { ticketNumber: number | null; title: string }>(
  a: T,
  b: T,
): number => {
  if (a.ticketNumber === null || b.ticketNumber === null) {
    if (a.ticketNumber === b.ticketNumber) {
      return a.title.localeCompare(b.title, 'sk');
    }
    return a.ticketNumber === null ? 1 : -1;
  }
  return a.ticketNumber - b.ticketNumber;
};

// Turns human ticket references in descriptions/comments into project-local
// deep links while leaving all other text untouched.
export const TicketText = ({
  children,
  projectId,
}: {
  children: string;
  projectId: number;
}) => {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  for (const match of children.matchAll(TICKET_REFERENCE)) {
    const index = match.index ?? 0;
    if (index > lastIndex) {
      parts.push(children.slice(lastIndex, index));
    }
    const ticketNumber = Number(match[1]);
    parts.push(
      <Link
        key={`${index}-${ticketNumber}`}
        to={ticketUrl(projectId, ticketNumber)}
        className="font-mono font-semibold text-brand-500 underline decoration-brand-500/30 underline-offset-2 hover:text-brand-600"
      >
        {ticketKey(ticketNumber)}
      </Link>,
    );
    lastIndex = index + match[0].length;
  }

  if (lastIndex < children.length) {
    parts.push(children.slice(lastIndex));
  }

  return (
    <>
      {parts.map((part, index) => (
        <Fragment key={index}>{part}</Fragment>
      ))}
    </>
  );
};
